<script setup lang="ts">
type NotificationType = 'info' | 'success' | 'warning' | 'error'

interface NotificationItemProps {
    // Toast-specific props
    id?: string
    message?: string
    progress?: number
    onClose?: (id: string) => void

    // Shared props
    type?: NotificationType

    // Inline/Alert-specific props
    variant?: 'toast' | 'inline'
    dismissible?: boolean
    icon?: string
    soft?: boolean
}

const props = withDefaults(defineProps<NotificationItemProps>(), {
    type: 'info',
    variant: 'toast',
    dismissible: true,
    icon: undefined,
    soft: true,
    id: undefined,
    message: undefined,
    progress: 0,
    onClose: undefined
})

const emit = defineEmits<{
    dismiss: []
}>()

const isVisible = ref(true)
const notificationRef = ref<HTMLDivElement | null>(null)

// Conditional composable usage only for toast variant
const hasGlobalNotifications = typeof useGlobalNotifications === 'function'
let registerNotificationElement: ((id: string, el: HTMLElement) => void) | undefined

if (props.variant === 'toast' && hasGlobalNotifications) {
    const globalNotifications = useGlobalNotifications()
    registerNotificationElement = globalNotifications.registerNotificationElement
}

// Handle animation for entering (only for toast)
onMounted(() => {
    if (props.variant === 'toast' && notificationRef.value && props.id) {
        // Register the element for exit animations
        if (registerNotificationElement) {
            registerNotificationElement(props.id, notificationRef.value)
        }

        // Initial state (for enter animation)
        notificationRef.value.style.opacity = '0'
        notificationRef.value.style.transform = 'translateX(20px)'

        // Trigger enter animation after a small delay
        setTimeout(() => {
            if (notificationRef.value) {
                notificationRef.value.style.opacity = '1'
                notificationRef.value.style.transform = 'translateX(0)'
            }
        }, 10)
    }
})

// Type-based configuration with soft/solid support
const typeConfig = computed(() => {
    switch (props.type) {
    case 'success':
        return {
            bg: props.soft ? 'bg-success-light' : 'bg-success',
            border: 'border-success',
            text: props.soft ? 'text-success-shade-dark' : 'text-white',
            progress: 'bg-base-300',
            icon: 'check'
        }
    case 'error':
        return {
            bg: props.soft ? 'bg-error-light' : 'bg-error',
            border: 'border-error',
            text: props.soft ? 'text-error-shade-dark' : 'text-white',
            progress: 'bg-base-300',
            icon: 'x'
        }
    case 'warning':
        return {
            bg: props.soft ? 'bg-warning-light' : 'bg-warning',
            border: 'border-warning',
            text: props.soft ? 'text-warning-shade-dark' : 'text-white',
            progress: 'bg-base-300',
            icon: 'triangle-alert'
        }
    case 'info':
    default:
        return {
            bg: props.soft ? 'bg-info-light' : 'bg-info',
            border: 'border-info',
            text: props.soft ? 'text-info-shade-dark' : 'text-white',
            progress: 'bg-base-300',
            icon: 'info'
        }
    }
})

const defaultIcon = computed(() => props.icon !== undefined ? props.icon : typeConfig.value.icon)

// Handle dismiss for both variants
const handleClose = () => {
    if (props.variant === 'toast' && props.onClose && props.id) {
        // Toast variant: call onClose callback
        props.onClose(props.id)
    } else {
        // Inline variant: hide and emit
        isVisible.value = false
        emit('dismiss')
    }
}
</script>

<template>
    <!-- Toast Variant (original behavior) -->
    <div
        v-if="variant === 'toast'"
        ref="notificationRef"
        :class="[
            'flex items-center p-4 pr-8 m-2 shadow-md w-full max-w-sm relative',
            'transition-all duration-300 ease-in-out',
            'bg-base-100',
            typeConfig.text
        ]"
        role="alert"
        aria-live="polite"
    >
        <!-- Icon (optional for toast) -->
        <FoundationIcon
            v-if="defaultIcon"
            :name="defaultIcon"
            class="w-5 h-5 shrink-0"
            aria-hidden="true"
        />

        <!-- Message -->
        <div class="ml-3 mr-6 flex-grow">
            <p>{{ message }}</p>
        </div>

        <!-- Close button -->
        <FoundationButton
            v-if="dismissible"
            size="small"
            square
            :aria-label="$t('notification.close')"
            class="absolute top-0 bottom-0 right-2 m-auto"
            @click="handleClose"
        >
            <FoundationIcon
                name="x"
                class="w-3 h-3"
                aria-hidden="true"
            />
        </FoundationButton>

        <!-- Progress bar -->
        <div
            v-if="progress !== undefined && progress > 0"
            class="absolute bottom-0 left-0 h-1"
            :class="typeConfig.progress"
            :style="{ width: `${progress}%` }"
        />
    </div>

    <!-- Inline Variant -->
    <div
        v-else-if="variant === 'inline' && isVisible"
        role="alert"
        :class="[
            'flex items-center gap-3 p-4 border',
            typeConfig.bg,
            typeConfig.border,
            typeConfig.text
        ]"
    >
        <!-- Icon -->
        <FoundationIcon
            v-if="defaultIcon"
            :name="defaultIcon"
            class="w-5 h-5 shrink-0 mt-0.5"
            aria-hidden="true"
        />

        <!-- Content -->
        <div class="flex-1 min-w-0">
            <slot>
                <span v-if="message">{{ message }}</span>
            </slot>
        </div>

        <!-- Dismiss Button -->
        <FoundationButton
            v-if="dismissible"
            size="small"
            square
            :aria-label="$t('notification.close')"
            class="shrink-0"
            :class="typeConfig.text"
            @click="handleClose"
        >
            <FoundationIcon
                name="x"
                class="w-3 h-3"
                aria-hidden="true"
            />
        </FoundationButton>
    </div>
</template>
